/*
  Ever Leaf — Lamp Scene (Pass 3A: 2048 Scene Foundation — CORRECTED).

  Owns the shared 2048x2048 scene asset loading and camera source-rect math
  only. Personalization (photo prep, tonal curve, Color Burn, masking, text,
  transform clamping) stays entirely inside ever-leaf-image-renderer.js /
  ever-leaf-personalizer.js and is untouched by this file.

  ARCHITECTURE LOCK (see PROJECT-STATE-HANDOFF.md "Pass 3 — Leaf Close-Up <->
  Full Lamp" and the Pass 3A correction brief): the camera must operate
  AFTER personalization state has been established in stable, camera-
  independent scene coordinates — never before it. Concretely:

    stable 2048 scene assets (uncropped)
      -> ever-leaf-image-renderer.js personalizes against the FULL scene
         (maskBounds/photoX/photoY/photoScale are fractions of the whole
         2048 scene, never of a camera window)
      -> the result is a stable, camera-independent composite
      -> THIS FILE's applyCamera() crops/scales a camera source-rect of
         that already-finished composite onto the visible canvas

  This file previously (first Pass 3A pass) cropped the scene assets to the
  Leaf Close-Up camera rect BEFORE handing them to the renderer — that made
  photoX/photoY/photoScale implicitly relative to the camera window, so a
  future camera change (Pass 3C interpolating toward a Full Lamp preset)
  would have silently redefined what those numbers mean and forced
  personalization geometry (maskBounds, the prepared photo's fit) to be
  recomputed on every camera-animation frame. That violated the locked
  "camera changes are VIEW-DIRTY only, never PERSONALIZATION-DIRTY"
  requirement. Corrected here: assets handed to the renderer are always the
  full, uncropped 2048 scene; the camera is applied strictly as a final,
  cheap, personalization-independent crop step.

  A "Leaf Local Space" image (the shape ever-leaf-image-renderer.js has
  always expected for leafBase/photoMask/light) is, after this correction,
  simply the FULL 2048 scene image itself — ctx.drawImage() scales whatever
  it's given to canvasSize x canvasSize regardless of the source's own
  pixel dimensions, so no cropping is needed (or wanted) at this stage.

  SECOND CORRECTION — coordinate-semantics (state space vs. scene space).
  Fixing the crop-before-personalization bug above was necessary but not
  sufficient: it made computeMaskBounds() run against the real, full 2048
  Photo Mask, which silently changed what photoX/photoY/photoScale MEANT
  (they became fractions of the whole 2048 scene instead of fractions of
  the pre-existing, already-approved "Personalization State Space" — see
  PERSONALIZATION_REFERENCE_RECT below). This file now models THREE
  independent spaces, per the locked architecture:
    A. Personalization / Interaction State Space — the pre-Pass-3 normalized
       convention photoX/photoY/photoScale, MOVE_STEP, ZOOM_STEP, the fixed
       scale pivot, Pass 2A limits, and the cart transform properties have
       always used. Owned by ever-leaf-personalizer.js's state; this file
       never writes to it, only maps to/from it.
    B. Scene Space — the one 2048x2048 physical artwork/product space,
       shared origin (0,0). Owned by this file.
    C. Camera Space — LEAF_CLOSEUP_CAMERA (and later a Full Lamp preset):
       view rectangles into Scene Space, applied only via applyCamera(),
       strictly after both A and B have already been resolved.
  PERSONALIZATION_REFERENCE_RECT is the fixed A<->B adapter. It is not a
  camera and is never derived from one.
*/
(function () {
  'use strict';

  if (window.EverLeafLampScene) return;

  /*
    4096 SCENE RESOLUTION MIGRATION - READ BEFORE CHANGING THIS NUMBER.

    SCENE_SIZE is NOT the runtime scene resolution and must not be treated as
    it. It is purely the AUTHORING UNIT of the LEAF_CLOSEUP_CAMERA calibration
    immediately below: that rect was measured in pixels on the 2048 master
    artwork, and this constant only converts it into the normalized 0..1 form
    the renderer actually consumes. It is deliberately left at 2048 while the
    scene ASSETS move to 4096.

    Why that is correct, and not an oversight: the runtime never asks how many
    pixels a scene asset has. applyCamera() builds its source rect from the
    live `sourceCanvas.width/height` times a normalized camera rect, and
    ever-leaf-image-renderer.js composites every scene layer with the
    full-canvas drawImage(img, 0, 0, size, size) form with no source rectangle
    at all - so a 2048, a 4096 or a MIXED layer set all land on exactly the
    same normalized geometry (measured: identical to 4 decimal places for both
    the Close-Up and Full Lamp presets). The offscreen working canvas is sized
    by computeWorkingSize() from the DISPLAY size, so a 4096 source also does
    not create a 4096 canvas.

    Consequence: changing SCENE_SIZE alone would silently HALVE the normalized
    Close-Up camera and zoom the product. Changing it to 4096 would only be
    valid together with doubling every LEAF_CLOSEUP_CAMERA component, and the
    net rendering effect of doing both is exactly zero. So it is left as the
    honest unit of its own calibration.
  */
  var SCENE_SIZE = 2048;

  /*
    Camera Preset A — Leaf Close-Up.

    An explicit, artwork-calibrated rectangle in 2048 scene-pixel space —
    NOT a generic centered crop, NOT purely-automatic mask-bounds
    derivation. Derivation (unchanged from the first Pass 3A pass — this
    calibration itself was correct; only WHEN it gets applied was wrong):
    - Horizontally/vertically anchored on ever-leaf-photo-mask.png's own
      luminance bounding-box center in the real 2048 Gray scene (~1011,
      ~1021 in scene pixels — computed the same way
      ever-leaf-image-renderer.js's computeMaskBounds() does).
    - Sized to the real measured ~1126x1126 Leaf working area.
    - Nudged ~60px upward and visually verified by compositing actual crops
      of ever-leaf-lamp-gray-blur.png + ever-leaf-lights-overlay.png at this
      rect: the full ceramic silhouette sits comfortably inside frame with
      balanced surrounding-lamp context on every side.

    The Full Lamp viewport preset (PASS 3B, below) is a second, independent
    rectangle into this SAME scene — not a second camera/scene system. See
    FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED's own comment.

    Both the raw scene-pixel rect and a SCENE_SIZE-normalized (0..1)
    version are exposed. The normalized form is what actually drives
    rendering (resolution-independent, same convention as every other
    normalized value in this codebase) — the raw form is kept only because
    it's the calibration's own natural unit and is easier to eyeball/tune.
  */
  var LEAF_CLOSEUP_CAMERA = { x: 448, y: 398, width: 1126, height: 1126 };
  var LEAF_CLOSEUP_CAMERA_NORMALIZED = {
    x: LEAF_CLOSEUP_CAMERA.x / SCENE_SIZE,
    y: LEAF_CLOSEUP_CAMERA.y / SCENE_SIZE,
    width: LEAF_CLOSEUP_CAMERA.width / SCENE_SIZE,
    height: LEAF_CLOSEUP_CAMERA.height / SCENE_SIZE
  };

  /*
    PASS 3B / SINGLE-VIEWPORT CORRECTION — Full Lamp Viewport preset.

    ARCHITECTURE CORRECTION NOTE: there is no second camera or second scene
    here. There is ONE 2048x2048 Scene Space (above) and ONE viewport
    rectangle concept, shared by both presets — Close-Up
    (LEAF_CLOSEUP_CAMERA_NORMALIZED, pre-existing Pass 3A calibration, left
    under its original name/identifiers to avoid unnecessary churn to
    already-shipped, user-approved code) and Full Lamp (this constant).
    Both are plain `{x, y, width, height}` rectangles in the exact same
    normalized Scene Space — "Camera A/B" was never more than an informal
    label for "the first viewport preset" / "the second viewport preset";
    the runtime never modeled two independent camera systems.

    Unlike the Close-Up preset, Full Lamp's rectangle is not a single fixed
    calibration baked into this file — it is merchant-tunable (Visualizer
    Calibration block: Full Lamp Viewport X/Y/Size, all percent-of-scene)
    so the merchant can adjust framing without a code change. This constant
    is only the SCHEMA/JS default (0,0,100% — the identity viewport, i.e.
    the full 2048x2048 scene) — the actual rect used at render time is
    always built fresh from live config via buildFullLampViewportNormalized(),
    never read from here directly.

    Default derivation (real evidence, not a guess): direct visual
    inspection of the real, current ever-leaf-lamp-gray-sharp.png /
    ever-leaf-lamp-gray-blur.png (both confirmed 2048x2048, fully opaque —
    see this pass's asset geometry audit) shows the complete lamp product
    (crystal peak, both ceramic side forms, full base) already comfortably
    centered within the full scene, with room-scene context filling the
    rest of the square canvas on every side — i.e. the full, uncropped
    scene IS already a well-composed "Full Lamp" product shot. No crop is
    needed to reach a correct default Full Lamp viewport.
  */
  var FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED = { x: 0, y: 0, width: 1, height: 1 };

  /*
    PERSONALIZATION_REFERENCE_RECT — the state<->scene adapter (Pass 3A
    coordinate-semantics correction).

    THIS IS NOT A CAMERA. It is a fixed, independent rectangle (2048
    scene-pixel space) that defines where the pre-existing, already-approved
    "Personalization State Space" — the normalized coordinate convention
    photoX/photoY/photoScale, MOVE_STEP, ZOOM_STEP, the fixed scale-pivot
    default (0.50, 0.45), and every Pass 2A limit have always been expressed
    in — sits inside the new 2048 scene. Camera Preset A (LEAF_CLOSEUP_CAMERA
    above) exists to FRAME the shot; this rect exists to preserve HISTORICAL
    INTERACTION SEMANTICS. They are calibrated independently, from different
    inputs, and must never be derived from one another even though they
    happen to overlap substantially (both are, unsurprisingly, roughly
    "the Leaf area plus some margin").

    Why this is needed: before Pass 3, computeMaskBounds() ran against the
    legacy 1060 Photo Mask, so photoX/photoY/photoScale were fractions of
    THAT mask's own canvas. Pass 3A's first correction pass made
    computeMaskBounds() run against the real, full 2048 Photo Mask instead
    (correctly, for camera-independence) — but left photoX/Y/Scale
    implicitly reinterpreted as fractions of the whole 2048 scene, a
    completely different normalization base (the mask now occupies ~20.65%
    of the scene's width, not ~36.89% of the old canvas's width). A stored
    photoScale of, say, 0.368 would then mean something like double its old
    effective size relative to the mask. This rect is the fix: personalization
    state stays expressed in the OLD, approved normalized space; this rect
    maps that space into the new scene ONLY at the moment scene-space
    geometry is actually needed (clamping-reference mask bounds one way,
    the render-time transform the other way).

    Calibration (real measured geometry, not guessed): computed via
    computeMaskBounds()'s own luminance-threshold algorithm (threshold 20)
    against BOTH the legacy asset (ever-leaf-personalizer-photo-mask.png,
    1060x1060) and the real new asset (ever-leaf-photo-mask.png, 2048x2048):
      legacy mask bounds (fractions of the legacy 1060 canvas):
        left=0.3160377358490566 top=0.39245283018867927
        right=0.6849056603773584 bottom=0.7613207547169811
        width=height=0.36886792452830186
      new mask bounds (fractions of the real 2048 scene):
        left=0.390625 top=0.3955078125
        right=0.59716796875 bottom=0.60205078125
        width=height=0.20654296875
    A single uniform affine (both mask regions are square, so scaleX=scaleY)
    that maps the legacy bounds exactly onto the new bounds:
      scale   = 0.20654296875 / 0.36886792452830186 = 0.5599374600383632
      originX = 0.390625 - 0.3160377358490566 * scale = 0.2136636329124041
      originY = 0.3955078125 - 0.39245283018867927 * scale = 0.17575877157928385
    Stored below at FULL float precision (not rounded to integer scene
    pixels the way LEAF_CLOSEUP_CAMERA is) — unlike a camera crop, where
    sub-pixel framing precision is visually meaningless, this rect directly
    determines interaction-state numbers the "must retain current meaning
    EXACTLY" lock applies to, so avoidable rounding error is eliminated
    rather than accepted. Round-trip verified exactly (to full float
    precision, diff < 1e-9) against the legacy mask's left/top/right/bottom/
    center — see coordinate-semantics-proof.js (the executable version of
    this check, run during the Pass 3A coordinate-semantics correction).
    Scene-pixel form (origin/scale * 2048) is exposed only for readability/
    debugging: x~=437.58 y~=359.95 width=height~=1146.75.
  */
  /* PERSONALIZATION REFERENCE RECT - RECALIBRATED FOR THE CURRENT SCENE
     COMPOSITION (Mobile Frame-Owned migration).

     This rect maps historical Personalization State Space into Scene Space and
     is the single owner of BOTH the customer photo's placement and the
     Dedication's position and font size. It is a composition constant, never a
     resolution constant - a pure raster upscale must never change it.

     The active Scene artwork was rebuilt at 4096: the art was NOT upscaled,
     it was placed at its original pixel size inside a doubled canvas. Measured
     with this project's own computeMaskBounds() (threshold 20, same luminance
     weights) the Photo Mask window's normalized extent therefore HALVED while
     its centre stayed put:
         legacy mask  left 0.390625  top 0.395508  width 0.206543
         active mask  left 0.445313  top 0.447754  width 0.103516
     A control test (old mask drawn at 50% scale, centred, differenced against
     the new mask) returned mean-absolute-difference 0.000, proving the exact
     relationship is
         newScene = 0.25 + oldScene * 0.5
     so the adapter is scale 0.5 with origin 0.25 on both axes - derived, not
     eyeballed. Predicted mask left/top from that adapter reproduce the
     measured values to 5e-07 and 0.

     Applying it to the previous rect
       { 0.2136636329124041, 0.17575877157928385, 0.5599374600383632 }
     gives the values below. The historical rect is kept in this comment so a
     future artwork change can be re-derived from the same basis. */
  var PERSONALIZATION_REFERENCE_RECT_NORMALIZED = {
    x: 0.3568318164562020,
    y: 0.3378793857896419,
    width: 0.2799687300191816,
    height: 0.2799687300191816
  };
  var PERSONALIZATION_REFERENCE_RECT = {
    x: PERSONALIZATION_REFERENCE_RECT_NORMALIZED.x * SCENE_SIZE,
    y: PERSONALIZATION_REFERENCE_RECT_NORMALIZED.y * SCENE_SIZE,
    width: PERSONALIZATION_REFERENCE_RECT_NORMALIZED.width * SCENE_SIZE,
    height: PERSONALIZATION_REFERENCE_RECT_NORMALIZED.height * SCENE_SIZE
  };

  /*
    Inverse-maps a SCENE-space maskBounds object (the shape
    ever-leaf-image-renderer.js's computeMaskBounds() returns — left/top/
    right/bottom/width/height/centerX/centerY, normalized 0..1 of the full
    2048 scene) back into legacy/Personalization-State-Space, through
    PERSONALIZATION_REFERENCE_RECT. Called once per asset load. The result
    is what ever-leaf-personalizer.js must pass to
    renderer.computeDefaultPhotoTransform()/clampPhotoTransform()/
    clampScaleOnly() — i.e. every place that clamps or defaults the STATE
    transform — so those functions keep operating in exactly the coordinate
    space they always have, unaware anything about Scene Space exists.
  */
  function mapLegacyMaskBoundsFromScene(sceneMaskBounds) {
    var ref = PERSONALIZATION_REFERENCE_RECT_NORMALIZED;
    var left = (sceneMaskBounds.left - ref.x) / ref.width;
    var top = (sceneMaskBounds.top - ref.y) / ref.height;
    var right = (sceneMaskBounds.right - ref.x) / ref.width;
    var bottom = (sceneMaskBounds.bottom - ref.y) / ref.height;
    return {
      left: left,
      top: top,
      right: right,
      bottom: bottom,
      width: right - left,
      height: bottom - top,
      centerX: (left + right) / 2,
      centerY: (top + bottom) / 2
    };
  }

  /*
    Forward-maps a legacy/Personalization-State-Space transform
    { x, y, scale } (exactly what state.photoX/photoY/photoScale and the
    cart's _ever_leaf_photo_x/y/scale already store, untouched by Pass 3A)
    into a SCENE-space transform, through PERSONALIZATION_REFERENCE_RECT.
    This is used ONLY at the final render call (never for clamping, never
    for state, never for cart serialization) — the one place actual scene-
    space geometry is needed to draw the photo in the right place against
    the full-scene working canvas.
  */
  function mapTransformToScene(transform) {
    var ref = PERSONALIZATION_REFERENCE_RECT_NORMALIZED;
    return {
      x: ref.x + transform.x * ref.width,
      y: ref.y + transform.y * ref.height,
      scale: transform.scale * ref.width
    };
  }

  /*
    PASS 3A FINAL CONSOLIDATED UI CORRECTION — Custom Pivot red marker
    coordinate mapping (Section 8 of the brief: "Do NOT approximate it
    using simple percentages of the visualizer DOM. Use the real coordinate
    chain."). Maps a Personalization State Space point (the SAME space
    Zoom Pivot X/Y, Center X/Y, and every clamp already live in) all the
    way to a 0..1 fraction WITHIN Camera A's cropped view — i.e. exactly
    what a CSS `left/top: N%` against the visible canvas-wrap needs, since
    applyCamera() crops+scales that same camera rect to fill the canvas
    1:1 (see this file's own applyCamera/computeWorkingSize).

    Reuses mapTransformToScene() UNCHANGED for the first leg (State Space
    -> Scene Space) — the exact same affine transform the photo/text
    already go through — rather than deriving any new mapping math, per
    the brief's explicit instruction not to invent new zoom/coordinate
    behavior. The second leg (Scene Space -> Camera-normalized) is the
    same crop-rect arithmetic applyCamera() itself uses internally.

    Returns { x, y } in 0..1 camera-normalized space. NOT clamped to
    0..1 — a pivot configured outside Camera A's crop legitimately maps
    outside the visible canvas; the caller may choose to hide the marker
    in that case rather than draw it off-canvas.
  */
  function mapPersonalizationPointToCameraNormalized(x, y, cameraNormalized) {
    var scenePoint = mapTransformToScene({ x: x, y: y, scale: 0 });
    return {
      x: (scenePoint.x - cameraNormalized.x) / cameraNormalized.width,
      y: (scenePoint.y - cameraNormalized.y) / cameraNormalized.height
    };
  }

  /*
    PASS 3A FINAL UX PASS — Photo Interaction Bounding Box coordinate
    mapping (Section 32-36 of the brief: the outline must be the actual
    transformed PHOTO rectangle, mapped through the real coordinate chain,
    never approximated from mask/canvas bounds; must use the SAME effective
    camera the real render uses, so Close-Up Vertical Offset can never
    desync it from the photo).

    `centerX/centerY/scale/aspect` are exactly photoCenterX/photoCenterY/
    photoScale/photoAspect from Personalization State Space (the same
    Center convention centerToTopLeft()/topLeftToCenter() in
    ever-leaf-image-renderer.js already use — width = scale, height =
    scale/aspect, top-left = center - half-extent).

    Top-left corner reuses mapPersonalizationPointToCameraNormalized()
    UNCHANGED (the same point-mapping the Custom Pivot marker uses) for its
    position leg. Width/height reuse mapTransformToScene()'s own "scale"
    leg (the same one Dedication's font-size ratios go through) — valid for
    ANY linear size in this space since PERSONALIZATION_REFERENCE_RECT and
    every camera rect used so far are square (scaleX === scaleY) — then
    divided by the camera's own width/height to land in the same 0..1
    camera-normalized space the marker's point mapping returns. NOT clamped
    to 0..1 — a photo scaled/positioned so its rect extends beyond Camera
    A's crop legitimately maps outside 0..1; the caller's CSS overlay is
    naturally clipped by the canvas-wrap's own overflow (Section 36 — never
    shrunk just to stay visible).
  */
  function computePhotoBoundingBoxCameraNormalized(centerX, centerY, scale, aspect, cameraNormalized) {
    var effectiveAspect = aspect || 1;
    var height = scale / effectiveAspect;
    var topLeftX = centerX - scale / 2;
    var topLeftY = centerY - height / 2;
    var topLeft = mapPersonalizationPointToCameraNormalized(topLeftX, topLeftY, cameraNormalized);
    var sceneWidth = mapTransformToScene({ x: 0, y: 0, scale: scale }).scale;
    var sceneHeight = mapTransformToScene({ x: 0, y: 0, scale: height }).scale;
    return {
      left: topLeft.x,
      top: topLeft.y,
      width: sceneWidth / cameraNormalized.width,
      height: sceneHeight / cameraNormalized.height
    };
  }

  function loadImage(url) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () {
        resolve(img);
      };
      img.onerror = function () {
        reject(new Error('EverLeafLampScene: failed to load asset ' + url));
      };
      img.src = url;
    });
  }

  // Pass 3A targeted correction — Scene Asset descriptor-fingerprint cache.
  // Previously ONE whole-bundle promise, cached forever regardless of the
  // `urls` passed in on a later call — root cause of a merchant's Scene
  // Asset picker change (e.g. Lamp Blur) not visually taking effect after
  // Save: a Theme Editor section reload re-runs init() with the NEWLY
  // resolved (already-2048-validated) Content URL, but the stale
  // first-ever-loaded bundle kept winning forever, silently ignoring it.
  // Fixed by caching each image INDIVIDUALLY, keyed by its own URL — an
  // unchanged URL (the common case: most reloads change nothing here)
  // still resolves instantly from cache; a genuinely different URL (a real
  // merchant change) is a cache miss and fetches fresh, with zero effect on
  // the OTHER two images if their URLs didn't change. This is a strictly
  // finer-grained fix than a whole-bundle fingerprint would be: e.g. a
  // Lights Overlay change alone re-fetches only the Lights image, not
  // Photo Mask or Lamp Blur too.
  var imageCache = {}; // url -> Promise<Image>
  function loadImageCached(url) {
    if (!imageCache[url]) {
      imageCache[url] = loadImage(url);
    }
    return imageCache[url];
  }
  // urls: { photoMask, lightsOverlay } — the two 2048x2048 scene assets
  // shared by EVERY variant's Scene (Section 15 of the PHASE 45B brief).
  // PHASE 45B: variant-specific Lamp Blur/Sharp pairs no longer load here —
  // they load per real selected variant via loadMaterialPair() below, keyed
  // by the variant's own Scene reference, never by a fixed "Gray" URL pair.
  function loadSceneAssets(urls) {
    return Promise.all([
      loadImageCached(urls.photoMask),
      loadImageCached(urls.lightsOverlay)
    ]).then(function (results) {
      return {
        photoMask: results[0],
        lightsOverlay: results[1]
      };
    });
  }

  /*
    Pass 3A entry point (CORRECTED), extended in Pass 3B, simplified in
    PHASE 45B. Returns a Promise resolving to { photoMask, light } — the
    two SHARED 2048 scene images every variant's Scene draws on top of.
    PHASE 45B: no longer returns leafBase/leafBaseSharp — those are
    variant-specific now (loaded per real selected variant via
    loadMaterialPair() below, keyed by that variant's own Scene reference)
    rather than a fixed Gray pair loaded unconditionally here. maskAlpha/
    maskBounds (computed downstream from photoMask alone) are therefore
    still identical regardless of which variant's Scene is active.
  */
  function getLeafSceneAssets(urls) {
    return loadSceneAssets(urls).then(function (scene) {
      return {
        photoMask: scene.photoMask,
        light: scene.lightsOverlay
      };
    });
  }

  /*
    PASS 3D, generalized in PHASE 45B — loads ONE variant's registered
    Blur/Sharp Scene pair, reusing the SAME per-URL `loadImageCached()`
    cache every other scene image already goes through. Every real
    variant's Scene (Gray included, as of PHASE 45B) is loaded identically
    through this one function, keyed by whatever URLs the caller resolved
    for that variant's Scene — this function itself has no notion of
    variant IDs, material names, or Shopify storage location. Calling this
    twice with the same URLs (e.g. re-selecting a variant already loaded)
    resolves instantly from the existing URL-keyed cache, never re-
    fetching or re-decoding. `materialUrls` is `{ lampBlurUrl, lampSharpUrl }`.
    Rejects (never crashes) if either URL fails to load — the caller
    (ever-leaf-personalizer.js) is responsible for catching this and
    preserving the current working Scene (a failed load must never take
    down the Visualizer or silently touch the real Shopify selected variant).
  */
  function loadMaterialPair(materialUrls) {
    return Promise.all([
      loadImageCached(materialUrls.lampBlurUrl),
      loadImageCached(materialUrls.lampSharpUrl)
    ]).then(function (results) {
      return { blurImage: results[0], sharpImage: results[1] };
    });
  }

  /*
    The ONLY function that knows about "camera". Crops/scales the
    `cameraNormalized` (0..1 of the full scene) sub-rectangle of an
    already-fully-rendered `sourceCanvas` onto `destCanvas` (caller must
    have already set destCanvas.width/height to the desired output size).
    Pure pixel copy — zero personalization logic, zero knowledge of photo/
    mask/text/light. This is deliberately the cheapest possible operation
    (one drawImage call) so that changing which camera rect is passed here
    is the ENTIRE cost of a view-only update.
  */
  function applyCamera(sourceCanvas, cameraNormalized, destCanvas) {
    var sw = sourceCanvas.width;
    var sh = sourceCanvas.height;
    var sx = cameraNormalized.x * sw;
    var sy = cameraNormalized.y * sh;
    var scw = cameraNormalized.width * sw;
    var sch = cameraNormalized.height * sh;
    var ctx = destCanvas.getContext('2d');
    ctx.drawImage(sourceCanvas, sx, sy, scw, sch, 0, 0, destCanvas.width, destCanvas.height);
    return destCanvas;
  }

  /*
    Given the DISPLAY raster size the visible canvas will actually render
    at, returns the full-scene OFFSCREEN working size needed so that, after
    applyCamera() crops it down to `cameraNormalized`, the result lands at
    native `displaySize` resolution — i.e. no quality loss vs. rendering
    the camera crop directly (which is what Pass 3A must visually match).
    Resolution-independent, same math regardless of interactive vs. HQ
    tier.
  */
  function computeWorkingSize(displaySize, cameraNormalized) {
    /* MOBILE FRAME-OWNED MIGRATION (Model B). The DESTINATION may now be
       rectangular, but the offscreen Scene composite stays SQUARE - lamp,
       Photo Mask, customer photo, Dedication and Lights all share one square
       Scene coordinate system and must keep sharing it. So this still returns
       ONE scalar: the square working size that makes the camera crop land at
       native destination resolution on its most demanding axis.

       `displaySize` accepts either the historical scalar or {width, height}.
       Because the camera's aspect now matches the destination's, the two
       terms below are normally equal; Math.max keeps it correct (never
       under-resolved) if a caller ever passes a mismatched pair. */
    var dw = typeof displaySize === 'number' ? displaySize : (displaySize && displaySize.width) || 0;
    var dh = typeof displaySize === 'number' ? displaySize : (displaySize && displaySize.height) || 0;
    if (!(dw > 0)) dw = dh;
    if (!(dh > 0)) dh = dw;
    var byWidth = cameraNormalized.width > 0 ? dw / cameraNormalized.width : dw;
    var byHeight = cameraNormalized.height > 0 ? dh / cameraNormalized.height : dh;
    return Math.max(1, Math.round(Math.max(byWidth, byHeight)));
  }

  /*
    PASS 3C — viewport interpolation (Section 4/10/11 of the brief). Pure,
    stateless linear interpolation between two normalized viewport rects
    (the SAME `{x,y,width,height}` shape every preset already uses — no
    separate "transition rect" type exists). `t` is the canonical
    normalized transition progress (0 = `from`, 1 = `to`); every field is
    derived directly from the two canonical endpoint rects and the current
    `t` — never from the previous frame's own output — so per-frame
    floating-point error can never accumulate/drift across a long-running
    or repeatedly-reversed animation (Section 11: "no accumulated
    geometry"). At t=0 this returns `from`'s own values exactly (0 * delta
    = 0); at t=1 it returns `to`'s own values exactly (1 * delta = the
    full delta) — both endpoints are therefore exact by construction, not
    merely close, which is what guarantees Section 7's "Pass 3B endpoints
    are frozen" requirement without any special-case branching in the
    caller.
  */
  function lerpViewportNormalized(fromViewport, toViewport, t) {
    var clampedT = typeof t === 'number' && isFinite(t) ? Math.max(0, Math.min(1, t)) : 0;
    return {
      x: fromViewport.x + (toViewport.x - fromViewport.x) * clampedT,
      y: fromViewport.y + (toViewport.y - fromViewport.y) * clampedT,
      width: fromViewport.width + (toViewport.width - fromViewport.width) * clampedT,
      height: fromViewport.height + (toViewport.height - fromViewport.height) * clampedT
    };
  }

  /*
    PASS 3A FINAL CONSOLIDATED CALIBRATION + FRAMING CORRECTION, Section
    38-50 — Close-Up Vertical Offset. Derives an EFFECTIVE camera rect from
    the locked base Camera A preset (LEAF_CLOSEUP_CAMERA_NORMALIZED, never
    overwritten) plus a merchant-facing vertical offset — never a separate
    per-layer translation (Section 39/45: Lamp Blur/Photo Mask/photo/
    Dedication/Lights all share one 2048 Scene Space and must stay locked
    together; shifting the CAMERA is what keeps them all moving as one).

    Merchant semantics (Section 41, defined FIRST, then converted — the
    merchant should never need to think like a camera operator): positive
    offsetPercent moves the entire visible composition DOWN within the
    fixed Visualizer window; negative moves it UP. Camera movement and
    apparent content movement are inverse, so a positive (content-down)
    offset is applied as a NEGATIVE shift to the camera's own source Y
    (moving the crop window itself up in Scene Space, which reveals more
    of what was above frame and pushes previously-framed content lower in
    the output) — this single sign inversion happens once, here, so every
    other caller (applyCamera, computeWorkingSize,
    mapPersonalizationPointToCameraNormalized) just receives an ordinary
    camera rect and needs no sign-awareness of its own.

    offsetPercent is relative to Camera A's own height (Section 43): +5
    means the composition shifts down by ~5% of Camera A's height.

    Safety clamp (Section 50): the effective camera's Y is clamped so the
    source rect never reads outside the real 2048 Scene bounds — the
    camera is never resized/stretched, only repositioned, and only within
    what the Scene actually contains.

    CENTER-ANCHORED / VIEW-FRAME-RELATIVE OFFSET PASS. offsetPercent and
    horizontalOffsetPercent are now scaled by the camera's own FINAL
    width/height (after zoom and destAspect, i.e. the rect that actually
    fills the View Frame), not by baseCameraNormalized's fixed unzoomed
    dimensions. Sections 43/Section-41-above still hold (percent, sign,
    positive=down/right) — only the divisor changed. Consequence: the
    Close-Up anchor (the base rect's own centre) sits at the exact View
    Frame centre at 0/0, at ~70% of the Frame's width at H=+20, ~30% at
    H=-20 (symmetric for V), and — because the divisor now scales WITH
    zoom exactly the way the visible Frame itself does — the same percent
    produces the same perceived on-screen displacement at any Zoom, until
    the reposition-only Scene-edge clamp below intervenes. Previously the
    divisor was zoom-INVARIANT (the base's own fixed size), so the same
    percent value produced a LARGER perceived shift at higher Zoom — this
    is the exact defect this pass corrects. Verified numerically (not just
    algebraically): `scratchpad/mobile-camera-center-anchor-pass/proof.js`.
  */
  function computeEffectiveCamera(baseCameraNormalized, offsetPercent, horizontalOffsetPercent, options) {
    var opts = options || {};
    var percent = typeof offsetPercent === 'number' && isFinite(offsetPercent) ? offsetPercent : 0;
    var hPercent = typeof horizontalOffsetPercent === 'number' && isFinite(horizontalOffsetPercent) ? horizontalOffsetPercent : 0;

    /* MOBILE FRAME-OWNED MIGRATION (Model B).

       `destAspect` (width/height of the visible canvas) makes the camera
       rectangular so a square Scene can be shown in a rectangular View Frame
       with NO stretching: the destination and the source rect share one
       aspect, so applyCamera()'s drawImage maps them 1:1.

       `zoom` replaces the old outer DOM surface multiplier. The previous
       Mobile architecture cropped TWICE - camera first, then a centred
       square surface clipped by the Frame - and that second crop is exactly
       a centred narrowing of the first. Collapsing them is closed-form:
           visibleFractionX = frameW / (zoom * frameW) = 1 / zoom
           visibleFractionY = frameH / (zoom * frameW)
           width  = base.width  * visibleFractionX = base.width / zoom
           height = base.height * visibleFractionY = width / destAspect
       i.e. the collapsed rect automatically carries the Frame's aspect,
       which is why one `zoom` number plus `destAspect` reproduces the old
       two-stage composition exactly (verified to 1e-16 against the real
       pre-migration geometry at 360/390/412/428 wide). */
    var zoom = typeof opts.zoom === 'number' && isFinite(opts.zoom) && opts.zoom > 0 ? opts.zoom : 1;
    var width = baseCameraNormalized.width / zoom;
    var destAspect = typeof opts.destAspect === 'number' && isFinite(opts.destAspect) && opts.destAspect > 0 ? opts.destAspect : 0;
    var height = destAspect > 0 ? width / destAspect : baseCameraNormalized.height / zoom;

    // Never larger than the Scene itself; keep the aspect while shrinking.
    // This must happen BEFORE the offsets below, since they are scaled by
    // these FINAL width/height values (the View-Frame-relative contract).
    if (width > 1) { height *= 1 / width; width = 1; }
    if (height > 1) { width *= 1 / height; height = 1; }

    // The anchor is the base rect's own centre (Close-Up's calibrated
    // leaf-focused point) — never recomputed from zoom/destAspect, so it
    // stays fixed in Scene Space regardless of framing.
    var anchorX = baseCameraNormalized.x + baseCameraNormalized.width / 2;
    var anchorY = baseCameraNormalized.y + baseCameraNormalized.height / 2;
    var centerX = anchorX - (hPercent / 100) * width;
    var centerY = anchorY - (percent / 100) * height;

    // Reposition-only clamp, exactly as before: the rect never resizes to fit,
    // it only slides back inside the Scene bounds.
    var effectiveX = Math.max(0, Math.min(1 - width, centerX - width / 2));
    var effectiveY = Math.max(0, Math.min(1 - height, centerY - height / 2));
    return { x: effectiveX, y: effectiveY, width: width, height: height };
  }

  /*
    SINGLE-VIEWPORT ARCHITECTURE CORRECTION (Pass 3B) — builds the Full Lamp
    viewport as a NORMALIZED (0..1 of the full scene) rect from the
    merchant's percent-based calibration settings, using the SAME
    `{ x, y, width, height }` viewport rectangle representation the Close-Up
    preset already uses (both presets feed the exact same
    applyCamera()/computeWorkingSize() below — there is only ever one
    viewport rectangle shape in this file). The viewport stays square: one
    "size" percent drives both width and height, so it can never distort.
    Pure math, no state/DOM knowledge — mirrors this file's own existing
    style (computeEffectiveCamera, applyCamera). Deliberately NOT clamped
    here — see clampViewportNormalized() below, called separately by the
    caller, matching the existing separation of concerns (this function
    computes the requested rect; clamping is a distinct, explicit safety
    step).
  */
  function buildFullLampViewportNormalized(xPercent, yPercent, sizePercent, destAspect) {
    var x = typeof xPercent === 'number' && isFinite(xPercent) ? xPercent / 100 : FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED.x;
    var y = typeof yPercent === 'number' && isFinite(yPercent) ? yPercent / 100 : FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED.y;
    var size =
      typeof sizePercent === 'number' && isFinite(sizePercent) && sizePercent > 0
        ? sizePercent / 100
        : FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED.width;
    /* MOBILE FRAME-OWNED MIGRATION (Model B). `size` is still ONE merchant
       control and still means "percent of the Scene", but it is now measured
       along the DESTINATION'S LONGER axis so it cannot distort a rectangular
       View Frame: portrait takes the height, landscape takes the width, and
       the other axis follows from the destination aspect. With no destAspect
       (Desktop, and any caller that has not opted in) this returns the exact
       previous square rect, so Desktop behaviour is untouched. */
    var a = typeof destAspect === 'number' && isFinite(destAspect) && destAspect > 0 ? destAspect : 0;
    var width = size;
    var height = size;
    if (a > 0) {
      if (a <= 1) { height = size; width = size * a; }
      else { width = size; height = size / a; }
    }
    return { x: x, y: y, width: width, height: height };
  }

  /*
    SINGLE-VIEWPORT ARCHITECTURE CORRECTION (Pass 3B) — safety validation/
    clamp for ANY viewport rect before it reaches
    applyCamera()/computeWorkingSize() (both do a raw drawImage source-rect
    calculation with no validation of their own; both are, and remain, the
    ONE shared entry point every preset's resolved rect passes through —
    there is no separate Close-Up-only or Full-Lamp-only rendering path).
    Guards against zero/negative/NaN width or height (falls back to the
    full-scene default rather than producing an invalid drawImage call),
    and repositions (never resizes/distorts) x/y so the source rect never
    reads outside the real 2048 Scene bounds — the exact same "reposition,
    never resize" safety philosophy computeEffectiveCamera() already uses
    for the Close-Up preset's vertical offset clamp. Real-world case this
    guards against: a merchant-saved Full Lamp Viewport of x=100%,y=100%,
    size=100% (a genuinely confusing but reachable Theme Editor input,
    found on this store's own DEV product.json during this correction pass)
    — width/height clamp to 1 (100%), which forces 1-width=0, so x/y clamp
    down to 0 regardless of the raw 100 saved — the viewport safely
    resolves to the full scene instead of an invalid/degenerate rect.
  */
  /* MOBILE CAMERA CONTROL UNIFICATION.

     Both Mobile views now expose the SAME merchant model - Zoom + Horizontal
     Offset + Vertical Offset - so they share one generic camera operation:

         base rect  ->  zoom  ->  composition offsets  ->  clamp

     The only difference is where the base comes from. Close-Up keeps its
     calibrated, leaf-focused anchor (LEAF_CLOSEUP_CAMERA_NORMALIZED adapted to
     the destination aspect inside computeEffectiveCamera) - it is deliberately
     NOT recentred on the whole Scene. Full Lamp's base is the aspect-fit rect
     below.

     CENTER-ANCHORED / VIEW-FRAME-RELATIVE OFFSET PASS: offsets are
     percentages of the view's own FINAL (post-zoom) dimensions — the rect
     that actually fills the View Frame — not the unzoomed base. H=V=0 puts
     the anchor (the base rect's own centre, which for Full Lamp's aspect-fit
     base is always exactly the Scene centre 0.5/0.5) at the exact View Frame
     centre; H=+20 puts it at ~70% of the Frame's visible width, H=-20 at
     ~30% (symmetric for V). Because the divisor now scales with zoom the
     same way the Frame itself does, the same percent produces the same
     perceived on-screen displacement at any Zoom (until the reposition-only
     Scene-edge clamp intervenes) — previously the divisor was the fixed
     unzoomed base, so the same value drifted to a larger perceived shift at
     higher Zoom. The minus signs are deliberate: a positive merchant value
     moves the visible COMPOSITION right/down, which requires moving the
     CAMERA left/up. The merchant never has to think about camera direction.
     Verified numerically: `scratchpad/mobile-camera-center-anchor-pass/proof.js`. */
  function computeAspectFitBaseViewport(destAspect) {
    var a = typeof destAspect === 'number' && isFinite(destAspect) && destAspect > 0 ? destAspect : 1;
    // The largest aspect-correct rect that fits entirely inside the square
    // Scene, centred. Nothing is ever stretched or letterboxed.
    if (a <= 1) return { x: (1 - a) / 2, y: 0, width: a, height: 1 };
    var h = 1 / a;
    return { x: 0, y: (1 - h) / 2, width: 1, height: h };
  }

  function computeCameraFromBase(baseRect, zoom, horizontalOffsetPercent, verticalOffsetPercent) {
    var z = typeof zoom === 'number' && isFinite(zoom) && zoom > 0 ? zoom : 1;
    var hPct = typeof horizontalOffsetPercent === 'number' && isFinite(horizontalOffsetPercent) ? horizontalOffsetPercent : 0;
    var vPct = typeof verticalOffsetPercent === 'number' && isFinite(verticalOffsetPercent) ? verticalOffsetPercent : 0;
    var width = baseRect.width / z;
    var height = baseRect.height / z;
    // Anchor = the base rect's own centre, fixed in Scene Space regardless
    // of zoom (for the aspect-fit base this is always exactly 0.5/0.5).
    // Offsets are scaled by the FINAL (post-zoom) width/height so the same
    // percent is the same fraction of the View Frame at any Zoom.
    var anchorX = baseRect.x + baseRect.width / 2;
    var anchorY = baseRect.y + baseRect.height / 2;
    var centerX = anchorX - (hPct / 100) * width;
    var centerY = anchorY - (vPct / 100) * height;
    // Reposition-only clamp; the rect never resizes to fit, so the aspect
    // established by the base can never be broken here.
    return clampViewportNormalized({ x: centerX - width / 2, y: centerY - height / 2, width: width, height: height });
  }

  // Full Lamp, Mobile: aspect-fit base + the unified Zoom/H/V controls.
  function buildFullLampCameraNormalized(zoom, horizontalOffsetPercent, verticalOffsetPercent, destAspect) {
    return computeCameraFromBase(computeAspectFitBaseViewport(destAspect), zoom, horizontalOffsetPercent, verticalOffsetPercent);
  }

  function clampViewportNormalized(viewport) {
    var MIN_SIZE = 0.01;
    var width =
      viewport && typeof viewport.width === 'number' && isFinite(viewport.width) && viewport.width > 0
        ? Math.min(1, viewport.width)
        : FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED.width;
    var height =
      viewport && typeof viewport.height === 'number' && isFinite(viewport.height) && viewport.height > 0
        ? Math.min(1, viewport.height)
        : width;
    width = Math.max(MIN_SIZE, width);
    height = Math.max(MIN_SIZE, height);
    var rawX = viewport && typeof viewport.x === 'number' && isFinite(viewport.x) ? viewport.x : 0;
    var rawY = viewport && typeof viewport.y === 'number' && isFinite(viewport.y) ? viewport.y : 0;
    var x = Math.max(0, Math.min(1 - width, rawX));
    var y = Math.max(0, Math.min(1 - height, rawY));
    return { x: x, y: y, width: width, height: height };
  }

  // DESKTOP PRODUCT SECTION PASS — Desktop Close-Up/Full Lamp Scale
  // Multiplier. Scales an ALREADY-FULLY-RESOLVED camera rect around its own
  // centre; never touches x/y anchor semantics or how that rect was built.
  // multiplier === 1 short-circuits to the exact input rect (by reference-
  // equal values, not just numerically equal) so the neutral value is
  // guaranteed byte-for-byte identical to the pre-existing Desktop view.
  // Desktop base rects (Close-Up and Full Lamp) are always square, so
  // clampViewportNormalized's independent width/height clamping below can
  // never introduce aspect distortion here.
  function applyScaleMultiplier(viewport, multiplier) {
    var m = typeof multiplier === 'number' && isFinite(multiplier) && multiplier > 0 ? multiplier : 1;
    if (m === 1 || !viewport) return viewport;
    var width = viewport.width / m;
    var height = viewport.height / m;
    var centerX = viewport.x + viewport.width / 2;
    var centerY = viewport.y + viewport.height / 2;
    return clampViewportNormalized({ x: centerX - width / 2, y: centerY - height / 2, width: width, height: height });
  }

  window.EverLeafLampScene = {
    SCENE_SIZE: SCENE_SIZE,
    LEAF_CLOSEUP_CAMERA: LEAF_CLOSEUP_CAMERA,
    LEAF_CLOSEUP_CAMERA_NORMALIZED: LEAF_CLOSEUP_CAMERA_NORMALIZED,
    FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED: FULL_LAMP_VIEWPORT_DEFAULT_NORMALIZED,
    PERSONALIZATION_REFERENCE_RECT: PERSONALIZATION_REFERENCE_RECT,
    PERSONALIZATION_REFERENCE_RECT_NORMALIZED: PERSONALIZATION_REFERENCE_RECT_NORMALIZED,
    getLeafSceneAssets: getLeafSceneAssets,
    loadMaterialPair: loadMaterialPair,
    applyCamera: applyCamera,
    computeWorkingSize: computeWorkingSize,
    computeEffectiveCamera: computeEffectiveCamera,
    buildFullLampViewportNormalized: buildFullLampViewportNormalized,
    clampViewportNormalized: clampViewportNormalized,
    applyScaleMultiplier: applyScaleMultiplier,
    computeAspectFitBaseViewport: computeAspectFitBaseViewport,
    computeCameraFromBase: computeCameraFromBase,
    buildFullLampCameraNormalized: buildFullLampCameraNormalized,
    lerpViewportNormalized: lerpViewportNormalized,
    mapLegacyMaskBoundsFromScene: mapLegacyMaskBoundsFromScene,
    mapTransformToScene: mapTransformToScene,
    mapPersonalizationPointToCameraNormalized: mapPersonalizationPointToCameraNormalized,
    computePhotoBoundingBoxCameraNormalized: computePhotoBoundingBoxCameraNormalized
  };
})();
